<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { intlLocale, t } from '../i18n';
import { formatCurrencySums, formatMoney, formatNumber, formatUnsignedPercent, toNumber } from '../format';
import { periodBuckets, periodDays, periodRange } from '../calendarPeriod';
import { isPhone } from '../isPhone';
import { passiveIncomeYear, portfolioTotals } from '../portfolioView';
// Phone: three cards (totals, chart, list); desktop: one bordered section as before.
import { CARD, CHIP_ROW, chipClass } from '../ui';

// Payout calendar of one portfolio view: paid in the past, known and forecast ahead
// (docs/cashflow/SPEC.md, decisions 11–19).
const props = defineProps({
  // Items of GET /cashflow/calendar narrowed to this portfolio
  items: {
    type: Array,
    required: true,
  },
  // Years for the period picker, newest first
  years: {
    type: Array,
    default: () => [],
  },
  // Moscow "today", YYYY-MM-DD
  today: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  // { period: 'month' | 'year', amount } or null
  goal: {
    type: Object,
    default: null,
  },
  // Items of the "next 12 months" period, whatever period is shown: the goal is measured on them
  // (docs/cashflow/SPEC.md, decision 25)
  forwardItems: {
    type: Array,
    default: () => [],
  },
  // The portfolio view with live prices and CBR rates: its value for the passive income %
  portfolio: {
    type: Object,
    default: null,
  },
  livePrices: {
    type: Object,
    default: () => ({}),
  },
  fxRates: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit-goal']);

const period = defineModel('period', { type: Object, required: true });

// Stack order bottom → top; dark-surface colors validated by the dataviz validator (all checks pass).
const SERIES = [
  { key: 'paid', color: '#199e70' },
  { key: 'declared', color: '#3987e5' },
  { key: 'forecast', color: '#9085e9' },
  { key: 'maturity', color: '#c98500' },
];

const INCOME = ['dividend', 'coupon'];
const TAX = ['dividend_tax', 'coupon_tax'];
const TAX_OF = { dividend_tax: 'dividend', coupon_tax: 'coupon' };

const range = computed(() => periodRange(period.value, props.today));

// --- Period controls ---------------------------------------------------------

const options = computed(() => {
  const years = new Set(props.years);
  if (period.value.preset === 'year') {
    years.add(period.value.year);
  }
  // In time order: past years and this one, "12 months ahead", next years (docs/design/SPEC.md, decision 19).
  const current = Number(props.today.slice(0, 4));
  const sorted = [...years].sort((a, b) => a - b);
  const year = (y) => ({ value: `year:${y}`, label: String(y) });
  return [
    ...sorted.filter((y) => y <= current).map(year),
    { value: 'next12', label: t('calendar.next12') },
    ...sorted.filter((y) => y > current).map(year),
  ];
});

// Past years are on the left, so the row opens scrolled to the chosen chip.
const chipRow = ref(null);

function revealSelectedChip() {
  nextTick(() => {
    chipRow.value?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest', inline: 'center' });
  });
}

onMounted(revealSelectedChip);
watch(() => options.value.length, revealSelectedChip);

const selectValue = computed(() => (period.value.preset === 'year' ? `year:${period.value.year}` : period.value.preset));

// Chips on the phone and on desktop alike; no custom dates (docs/cashflow/SPEC.md, decision 31).
function choosePeriod(value) {
  period.value = value.startsWith('year:') ? { preset: 'year', year: Number(value.slice(5)) } : { preset: value };
}

// --- Sums --------------------------------------------------------------------

// [currency, amount]: RUB at the CBR rate when converted, else the original currency (docs/fx/SPEC.md).
function moneyOf(item) {
  const rub = toNumber(item.amount_rub);
  if (rub !== null) {
    return ['RUB', rub];
  }
  const amount = toNumber(item.amount);
  return amount === null ? null : [item.currency, amount];
}

// What a day row shows: a payout with its tax merged in is shown after tax.
function rowMoney(item) {
  const money = moneyOf(item);
  if (money === null || !item.tax || item.tax[0] !== money[0]) {
    return money;
  }
  return [money[0], money[1] + item.tax[1]];
}

function seriesOf(item) {
  if (item.kind === 'maturity') {
    return 'maturity';
  }
  return ['paid', 'forecast'].includes(item.status) ? item.status : 'declared';
}

function addTo(map, currency, amount) {
  map.set(currency, (map.get(currency) ?? 0) + amount);
}

const totals = computed(() => {
  const sums = { total: new Map(), dividends: new Map(), coupons: new Map(), tax: new Map(), maturity: new Map() };
  for (const item of props.items) {
    const money = moneyOf(item);
    if (money === null) {
      continue;
    }
    if (TAX.includes(item.kind)) {
      addTo(sums.tax, ...money);
    } else if (item.kind === 'maturity') {
      addTo(sums.maturity, ...money);
    } else if (INCOME.includes(item.kind)) {
      addTo(sums.total, ...money);
      addTo(item.kind === 'dividend' ? sums.dividends : sums.coupons, ...money);
    }
  }
  const months = Math.max(1, periodDays(range.value) / (365 / 12));
  const monthly = new Map([...sums.total.entries()].map(([c, a]) => [c, a / months]));
  return { ...sums, monthly };
});

// Passive income as in the analytics summary: next 12 months after tax, to the current value
// (docs/cashflow/SPEC.md, decision 30).
const passivePercent = computed(() => {
  const total = props.portfolio ? portfolioTotals(props.portfolio, props.livePrices, props.fxRates).total : null;
  return total ? (passiveIncomeYear(props.forwardItems) / total) * 100 : null;
});

// --- Goal (decision 25): always the next 12 months, "Total" as the calendar shows it --------
// A monthly goal is compared with the average per month, a yearly one with the 12-month total.

const forwardIncome = computed(() => props.forwardItems
  .filter((item) => INCOME.includes(item.kind))
  .map(moneyOf)
  .filter((money) => money && money[0] === 'RUB')
  .reduce((sum, [, amount]) => sum + amount, 0));

const goalTarget = computed(() => toNumber(props.goal?.amount) || null);

const goalValue = computed(() => (props.goal?.period === 'year' ? forwardIncome.value : forwardIncome.value / 12));

const progress = computed(() => (goalTarget.value ? (goalValue.value / goalTarget.value) * 100 : null));

const goalLabel = computed(() => (props.goal
  ? t(`goal.label.${props.goal.period}`, { amount: formatMoney(props.goal.amount, 'RUB') })
  : t('goal.set')));

// --- Chart (decision 18) -------------------------------------------------------

function bucketKey(date, unit) {
  return unit === 'year' ? date.slice(0, 4) : date.slice(0, 7);
}

function bucketTitle(bucket) {
  if (bucket.unit === 'year') {
    return bucket.key;
  }
  const [y, m] = bucket.key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' });
}

function bucketLabel(bucket) {
  if (bucket.unit === 'year') {
    return bucket.key;
  }
  const [y, m] = bucket.key.split('-').map(Number);
  // Three letters fit a bar on a phone ("окт", "ноя"); the year goes under January (docs/design/SPEC.md).
  return new Date(y, m - 1, 1).toLocaleDateString(intlLocale(), { month: 'short' }).replace('.', '').slice(0, 3);
}

function bucketYear(bucket) {
  const [y, m] = bucket.key.split('-').map(Number);
  return bucket.unit !== 'year' && m === 1 ? String(y) : null;
}

const chart = computed(() => {
  const buckets = periodBuckets(range.value).map((bucket) => ({
    ...bucket,
    title: bucketTitle(bucket),
    label: bucketLabel(bucket),
    year: bucketYear(bucket),
    values: Object.fromEntries(SERIES.map((s) => [s.key, 0])),
  }));
  const byKey = new Map(buckets.map((b) => [b.key, b]));
  const unit = buckets[0]?.unit ?? 'month';
  for (const item of props.items) {
    const money = moneyOf(item);
    const bucket = byKey.get(bucketKey(item.date, unit));
    if (bucket && money && money[0] === 'RUB' && !TAX.includes(item.kind)) {
      bucket.values[seriesOf(item)] += money[1];
    }
  }
  const totalsOf = buckets.map((b) => SERIES.reduce((sum, s) => sum + b.values[s.key], 0));
  const scale = chartScale(Math.max(0, ...totalsOf));
  return {
    // Dashed grid lines, % of the plot height from the bottom
    grid: scale.lines.map((value) => ({ value, bottom: (value / scale.max) * 100 })),
    buckets: buckets.map((bucket, index) => ({
      ...bucket,
      total: totalsOf[index],
      height: (totalsOf[index] / scale.max) * 100,
      segments: SERIES.filter((s) => bucket.values[s.key] > 0).map((s) => ({ ...s, value: bucket.values[s.key] })),
    })),
  };
});

// A round grid step (1, 2 or 5 × 10ⁿ) giving about four lines: 100, 20 000… (decision 30).
function chartScale(max) {
  if (max <= 0) {
    return { max: 1, lines: [] };
  }
  const raw = max / 4;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].find((m) => m * power >= raw) * power;
  const top = Math.ceil(max / step) * step;
  const lines = [];
  for (let i = 1; i * step <= top + step / 2; i += 1) {
    lines.push(i * step);
  }
  return { max: top, lines };
}

const hovered = ref(null);
const selectedBucket = ref(null);
watch(range, () => { selectedBucket.value = null; });

// A second tap on the chosen bar clears the choice; the list then shows the current month again.
function toggleBucket(key) {
  selectedBucket.value = selectedBucket.value === key ? null : key;
}

const activeBucket = computed(() => {
  const list = chart.value.buckets;
  return list.find((b) => b.key === selectedBucket.value)
    ?? list.find((b) => b.key === bucketKey(props.today, b.unit))
    ?? list[0];
});

// The legend lists only the series that have payouts in the period.
const presentSeries = computed(() => SERIES.filter((s) => chart.value.buckets.some((b) => b.values[s.key] > 0)));

// Short sums over the bars and on the grid: 249, 6.4K, 120K, 1.3M (decision 30).
function compact(value) {
  const short = (n, suffix) => `${n < 10 ? Number(n.toFixed(1)) : Math.round(n)}${suffix}`;
  if (value >= 999_500) {
    return short(value / 1_000_000, 'M');
  }
  if (value >= 1000) {
    return short(value / 1000, 'K');
  }
  return String(Math.round(value));
}

// --- Lists ---------------------------------------------------------------------

// The active bucket's items by day; one row per instrument, kind, status and day (accounts summed).
const bucketDays = computed(() => {
  const bucket = activeBucket.value;
  if (!bucket) {
    return [];
  }
  const rows = new Map();
  for (const item of props.items) {
    if (bucketKey(item.date, bucket.unit) !== bucket.key) {
      continue;
    }
    const key = `${item.date}|${item.instrument_uid}|${item.kind}|${item.status}`;
    if (!rows.has(key)) {
      rows.set(key, { ...item, key, amount: null, amount_rub: null, quantity: null });
    }
    const row = rows.get(key);
    for (const field of ['amount', 'amount_rub', 'quantity']) {
      const value = toNumber(item[field]);
      if (value !== null) {
        row[field] = (row[field] ?? 0) + value;
      }
    }
  }
  // The tax withheld from a payout goes into the payout's row (same day and security): one line with
  // the amount after tax (docs/cashflow/SPEC.md, decision 26).
  for (const row of [...rows.values()]) {
    const income = TAX_OF[row.kind] && [...rows.values()].find((r) => r.kind === TAX_OF[row.kind]
      && r.date === row.date && r.instrument_uid === row.instrument_uid && !TAX.includes(r.kind));
    if (!income) {
      continue;
    }
    const tax = moneyOf(row);
    if (tax) {
      income.tax = income.tax ? [income.tax[0], income.tax[1] + tax[1]] : tax;
    }
    rows.delete(row.key);
  }
  const days = new Map();
  for (const row of [...rows.values()].sort((a, b) => a.date.localeCompare(b.date))) {
    if (!days.has(row.date)) {
      days.set(row.date, []);
    }
    days.get(row.date).push(row);
  }
  return [...days.entries()].map(([date, list]) => ({ date, items: list }));
});

const bucketIncome = computed(() => {
  const sums = new Map();
  for (const day of bucketDays.value) {
    for (const item of day.items) {
      const money = moneyOf(item);
      if (money && INCOME.includes(item.kind)) {
        addTo(sums, ...money);
      }
    }
  }
  return sums;
});

function dayLabel(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short', weekday: 'short' });
}

function amountClass(item) {
  if (moneyOf(item) === null) {
    return 'text-slate-500';
  }
  if (TAX.includes(item.kind)) {
    return 'text-rose-400';
  }
  // Green is kept for the headline total only (docs/design/SPEC.md, decision 2).
  return 'text-slate-100';
}

function hideLogo(event) {
  event.target.style.display = 'none';
}

// Every payout row says where it stands; colors follow the chart series (docs/cashflow/SPEC.md, decision 29).
const STATUS_CLASS = {
  paid: 'bg-emerald-900/40 text-emerald-300',
  declared: 'bg-blue-900/40 text-blue-300',
  awaiting: 'bg-blue-900/40 text-blue-300',
  recommended: 'bg-blue-900/40 text-blue-300',
  forecast: 'bg-violet-900/40 text-violet-300',
  unknown: 'bg-slate-800 text-slate-400',
};

// Taxes carry no badge: they are shown inside their payout's row.
function statusClass(item) {
  return TAX.includes(item.kind) ? null : STATUS_CLASS[item.status] ?? null;
}
</script>

<template>
  <section class="space-y-3 sm:space-y-0 sm:rounded-lg sm:border sm:border-slate-700 sm:bg-slate-900/40">
    <header :class="[CARD, 'sm:border-b sm:border-slate-800']">
      <h3
        v-if="!isPhone"
        class="mb-3 text-base font-semibold text-slate-100"
      >
        {{ t('calendar.title') }}
      </h3>
      <!-- Period chips on the phone and on desktop; no arrows and no custom dates (docs/cashflow/SPEC.md, decision 31) -->
      <div
        ref="chipRow"
        :class="CHIP_ROW"
        role="tablist"
        :aria-label="t('calendar.period')"
      >
        <button
          v-for="option in options"
          :key="option.value"
          type="button"
          role="tab"
          :aria-selected="selectValue === option.value"
          :class="[chipClass(selectValue === option.value), selectValue === option.value ? '' : 'sm:hover:bg-slate-700']"
          @click="choosePeriod(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div
        class="mt-4"
        :class="loading ? 'opacity-60' : ''"
      >
        <p class="text-[0.8125rem] text-slate-500 sm:text-xs">
          {{ t('calendar.total') }}
        </p>
        <p class="mt-0.5 flex flex-wrap items-baseline gap-x-3 gap-y-1 tabular-nums">
          <span class="text-[1.75rem] font-semibold leading-9 text-emerald-400 sm:text-2xl">{{ formatCurrencySums(totals.total) }}</span>
          <span class="w-full text-[0.9375rem] text-slate-300 sm:w-auto sm:text-sm">{{ t('calendar.perMonthInline', { amount: formatCurrencySums(totals.monthly) }) }}</span>
        </p>
        <dl class="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-[0.8125rem] sm:mt-3 sm:grid-cols-5 sm:gap-y-2 sm:text-xs">
          <div>
            <dt class="text-slate-500">
              {{ t('calendar.dividends') }}
            </dt>
            <dd class="mt-0.5 tabular-nums text-slate-100">
              {{ formatCurrencySums(totals.dividends) }}
            </dd>
          </div>
          <div>
            <dt class="text-slate-500">
              {{ t('calendar.coupons') }}
            </dt>
            <dd class="mt-0.5 tabular-nums text-slate-100">
              {{ formatCurrencySums(totals.coupons) }}
            </dd>
          </div>
          <div v-if="totals.maturity.size > 0">
            <dt
              class="text-slate-500"
              :title="t('calendar.maturityHint')"
            >
              {{ t('calendar.maturities') }}
            </dt>
            <dd class="mt-0.5 tabular-nums text-slate-100">
              {{ formatCurrencySums(totals.maturity) }}
            </dd>
          </div>
          <div v-if="passivePercent !== null">
            <dt
              class="text-slate-500"
              :title="t('calendar.passiveHint')"
            >
              {{ t('summary.passive') }}
            </dt>
            <dd class="mt-0.5 tabular-nums text-slate-100">
              {{ formatUnsignedPercent(passivePercent) }}
            </dd>
          </div>
          <!-- "12 months ahead" is about income to come: no tax line (docs/design/SPEC.md, decision 21) -->
          <div v-if="totals.tax.size > 0 && period.preset !== 'next12'">
            <dt
              class="text-slate-500"
              :title="t('calendar.taxHint')"
            >
              {{ t('calendar.tax') }}
            </dt>
            <dd class="mt-0.5 tabular-nums text-rose-400">
              {{ formatCurrencySums(totals.tax) }}
            </dd>
          </div>
        </dl>
      </div>

      <!-- Phone: the goal is one row that opens its sheet -->
      <button
        v-if="isPhone"
        type="button"
        class="-mx-2 mt-3 block min-h-11 w-[calc(100%+1rem)] rounded-2xl px-2 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-800"
        @click="emit('edit-goal')"
      >
        <template v-if="progress !== null">
          <span class="flex items-baseline justify-between gap-3 text-[0.8125rem] tabular-nums">
            <span class="min-w-0 text-slate-400">{{ t(`goal.progress.${goal.period}`, { value: formatMoney(goalValue, 'RUB'), target: formatMoney(goalTarget, 'RUB') }) }}</span>
            <span
              class="shrink-0"
              :class="progress >= 100 ? 'text-emerald-400' : 'text-slate-100'"
            >{{ formatNumber(progress, progress < 10 ? 1 : 0) }} %</span>
          </span>
          <span
            class="mt-2 block h-1.5 overflow-hidden rounded-full bg-slate-800"
            role="progressbar"
            :aria-valuenow="Math.round(progress)"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-label="t('goal.title')"
          >
            <span
              class="block h-full rounded-full bg-emerald-500"
              :style="{ width: `${Math.min(100, Math.max(0, progress))}%` }"
            />
          </span>
        </template>
        <span
          v-else
          class="flex items-center justify-between text-[0.9375rem] text-slate-400"
        >
          {{ t('goal.setMobile') }}
          <span aria-hidden="true">›</span>
        </span>
      </button>

      <template v-else>
        <div class="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs">
          <button
            type="button"
            class="text-slate-500 underline decoration-dotted underline-offset-2 hover:text-slate-300"
            @click="emit('edit-goal')"
          >
            {{ goalLabel }}
          </button>
          <span
            v-if="progress !== null"
            class="tabular-nums text-slate-400"
          >
            {{ t(`goal.progress.${goal.period}`, { value: formatMoney(goalValue, 'RUB'), target: formatMoney(goalTarget, 'RUB') }) }}
            · <span :class="progress >= 100 ? 'text-emerald-400' : 'text-slate-200'">{{ formatNumber(progress, progress < 10 ? 1 : 0) }} %</span>
          </span>
        </div>
        <div
          v-if="progress !== null"
          class="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-800"
          role="progressbar"
          :aria-valuenow="Math.round(progress)"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="t('goal.title')"
        >
          <div
            class="h-full rounded-full bg-emerald-500"
            :style="{ width: `${Math.min(100, Math.max(0, progress))}%` }"
          />
        </div>
      </template>
    </header>

    <p
      v-if="loading && items.length === 0"
      :class="[CARD, 'text-sm text-slate-500 sm:px-5 sm:py-4']"
    >
      {{ t('common.loading') }}
    </p>
    <p
      v-else-if="items.length === 0"
      :class="[CARD, 'text-sm text-slate-500 sm:px-5 sm:py-4']"
    >
      {{ isPhone ? t('calendar.emptyMobile') : t('calendar.empty') }}
    </p>

    <template v-else>
      <div :class="[CARD, 'sm:border-b sm:border-slate-800 sm:px-5 sm:pb-3 sm:pt-4']">
        <!-- Taller plot with a round dashed grid and the sum over each bar (docs/cashflow/SPEC.md, decision 30) -->
        <div class="flex gap-1.5">
          <div
            class="relative w-7 shrink-0 text-right text-[0.625rem] leading-none tabular-nums text-slate-500"
            aria-hidden="true"
          >
            <div class="absolute inset-x-0 bottom-0 top-5">
              <span
                v-for="line in chart.grid"
                :key="line.value"
                class="absolute right-0 translate-y-1/2"
                :style="{ bottom: `${line.bottom}%` }"
              >{{ compact(line.value) }}</span>
            </div>
          </div>
          <div class="min-w-0 flex-1">
            <div class="relative h-60 sm:h-64">
              <div
                class="pointer-events-none absolute inset-x-0 bottom-0 top-5 border-b border-slate-700"
                aria-hidden="true"
              >
                <span
                  v-for="line in chart.grid"
                  :key="line.value"
                  class="absolute inset-x-0 border-t border-dashed border-slate-700/70"
                  :style="{ bottom: `${line.bottom}%` }"
                />
              </div>
              <div
                class="relative flex h-full items-end gap-1"
                role="list"
                :aria-label="t('calendar.chartLabel')"
                @mouseleave="hovered = null"
              >
                <!-- The chosen month is highlighted; a second tap clears it -->
                <button
                  v-for="bucket in chart.buckets"
                  :key="bucket.key"
                  type="button"
                  role="listitem"
                  class="relative flex h-full min-w-0 flex-1 flex-col justify-end rounded-lg pt-5"
                  :class="bucket.key === selectedBucket ? 'bg-slate-700/50' : 'hover:bg-slate-800/40'"
                  :aria-label="`${bucket.title}: ${formatMoney(bucket.total, 'RUB')}`"
                  :aria-pressed="bucket.key === selectedBucket"
                  @click="toggleBucket(bucket.key)"
                  @mouseenter="hovered = bucket.key"
                  @focus="hovered = bucket.key"
                >
                  <span
                    v-if="bucket.total > 0 && chart.buckets.length <= 18"
                    class="mb-1 shrink-0 whitespace-nowrap text-center text-[0.625rem] leading-none tabular-nums"
                    :class="bucket.key === selectedBucket ? 'font-semibold text-slate-100' : 'text-slate-400'"
                  >{{ compact(bucket.total) }}</span>
                  <span
                    v-if="bucket.total > 0"
                    class="mx-auto flex w-3/5 max-w-8 shrink-0 flex-col-reverse gap-0.5"
                    :style="{ height: `max(${bucket.height}%, 3px)` }"
                  >
                    <span
                      v-for="(segment, index) in bucket.segments"
                      :key="segment.key"
                      class="block min-h-0.5 w-full"
                      :class="index === bucket.segments.length - 1 ? 'rounded-t' : ''"
                      :style="{ flex: `${segment.value} 1 0`, backgroundColor: segment.color }"
                    />
                  </span>
                  <span
                    v-if="hovered === bucket.key && bucket.total > 0 && !isPhone"
                    class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 w-max -translate-x-1/2 rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-left text-[0.6875rem] shadow-xl"
                  >
                    <span class="block font-medium text-slate-100 first-letter:uppercase">{{ bucket.title }}</span>
                    <span
                      v-for="segment in bucket.segments"
                      :key="segment.key"
                      class="mt-0.5 flex items-center gap-1.5 tabular-nums text-slate-300"
                    >
                      <span
                        class="size-2 rounded-sm"
                        :style="{ backgroundColor: segment.color }"
                      />
                      {{ t(`calendar.series.${segment.key}`) }} — {{ formatMoney(segment.value, 'RUB') }}
                    </span>
                  </span>
                </button>
              </div>
            </div>
            <div class="mt-1 flex gap-1">
              <span
                v-for="bucket in chart.buckets"
                :key="bucket.key"
                class="min-w-0 flex-1 overflow-hidden text-center text-[0.625rem] leading-tight"
                :class="bucket.key === selectedBucket ? 'font-medium text-slate-100 underline decoration-emerald-400 decoration-2 underline-offset-4' : 'text-slate-500'"
              >{{ bucket.label }}<span
                v-if="bucket.year"
                class="hidden text-slate-600 sm:block"
              >{{ bucket.year }}</span></span>
            </div>
          </div>
        </div>
        <ul class="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[0.8125rem] text-slate-400 sm:mt-3 sm:gap-y-1 sm:text-[0.6875rem]">
          <li
            v-for="series in presentSeries"
            :key="series.key"
            class="flex items-center gap-1.5"
          >
            <span
              class="size-2.5 rounded-sm"
              :style="{ backgroundColor: series.color }"
            />
            {{ t(`calendar.series.${series.key}`) }}
          </li>
        </ul>
      </div>

      <div :class="[CARD, 'sm:px-5 sm:py-3']">
        <!-- One list, by day; the "by asset" tab was removed (docs/cashflow/SPEC.md, decision 32) -->
        <p
          v-if="activeBucket"
          class="mb-3 flex items-baseline justify-between gap-3 tabular-nums sm:mb-0"
        >
          <span class="min-w-0 truncate text-[1.0625rem] font-semibold text-slate-100 first-letter:uppercase sm:text-sm sm:font-normal sm:text-slate-300">{{ activeBucket.title }}</span>
          <span class="shrink-0 text-[0.9375rem] text-slate-100 sm:text-sm">{{ formatCurrencySums(bucketIncome) }}</span>
        </p>
        <p
          v-if="bucketDays.length === 0"
          class="mt-3 text-[0.8125rem] text-slate-500 sm:text-xs"
        >
          {{ t('calendar.bucketEmpty') }}
        </p>
        <div
          v-for="day in bucketDays"
          :key="day.date"
          class="mt-4 sm:mt-3"
        >
          <p class="text-[0.75rem] uppercase tracking-wide text-slate-500 sm:text-[0.6875rem]">
            {{ dayLabel(day.date) }}
          </p>
          <ul>
            <!-- First lines on one line left and right; the second line keeps its place (docs/design/SPEC.md, decision 14) -->
            <li
              v-for="item in day.items"
              :key="item.key"
              class="flex items-start gap-3 border-t border-slate-800/60 py-3 first:border-t-0 sm:py-2"
            >
              <span
                class="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-700 text-[0.6875rem] font-semibold text-white sm:size-7"
                :style="item.logo_color ? { backgroundColor: item.logo_color } : null"
              >
                {{ (item.name || item.ticker || '?').trim().charAt(0).toUpperCase() }}
                <img
                  v-if="item.logo_url"
                  :src="item.logo_url"
                  alt=""
                  loading="lazy"
                  class="absolute inset-0 size-full object-cover"
                  @error="hideLogo"
                >
              </span>
              <div class="min-w-0 flex-1">
                <p class="truncate text-[0.9375rem] leading-5 text-slate-100 sm:text-sm">
                  {{ item.name || item.ticker || t('calendar.noInstrument') }}<span
                    v-if="item.name && item.ticker"
                    class="hidden sm:inline"
                  ><span class="px-1.5 text-xs text-slate-600">|</span><span class="text-xs text-slate-500">{{ item.ticker }}</span></span>
                </p>
                <!-- One line: the badge never wraps under the kind (docs/design/SPEC.md, decision 14) -->
                <p class="mt-0.5 flex items-center gap-x-1.5 whitespace-nowrap text-[0.8125rem] leading-4 text-slate-400 sm:flex-wrap sm:gap-y-0.5 sm:whitespace-normal sm:text-xs">
                  <span class="min-w-0 truncate">{{ t(`calendar.kind.${item.kind}`) }}</span>
                  <span
                    v-if="statusClass(item)"
                    class="shrink-0 rounded px-1.5 py-px text-[0.6875rem] sm:text-[0.625rem]"
                    :class="statusClass(item)"
                    :title="t(`calendar.status.${item.status}Hint`)"
                  >{{ t(`calendar.status.${item.status}`) }}</span>
                </p>
              </div>
              <div class="shrink-0 text-right tabular-nums">
                <p
                  class="text-[0.9375rem] font-semibold leading-5 sm:text-sm"
                  :class="amountClass(item)"
                >
                  {{ rowMoney(item) === null ? t('calendar.amountUnknown') : formatMoney(rowMoney(item)[1], rowMoney(item)[0]) }}
                </p>
                <p
                  v-if="item.tax && moneyOf(item) !== null"
                  class="mt-0.5 text-[0.75rem] leading-4 text-slate-500 sm:text-[0.6875rem]"
                >
                  <!-- Phone: only the tax; the amount before tax is the sum of the two -->
                  {{ isPhone
                    ? t('calendar.taxOnly', { tax: formatMoney(Math.abs(item.tax[1]), item.tax[0]) })
                    : t('calendar.withTax', { gross: formatMoney(moneyOf(item)[1], moneyOf(item)[0]), tax: formatMoney(Math.abs(item.tax[1]), item.tax[0]) }) }}
                </p>
                <p
                  v-if="moneyOf(item) !== null && (item.per_unit !== null || item.currency !== 'RUB')"
                  class="mt-0.5 text-[0.75rem] leading-4 text-slate-500 sm:text-[0.6875rem]"
                >
                  <template v-if="item.currency !== 'RUB' && moneyOf(item)[0] === 'RUB'">
                    {{ formatMoney(item.amount, item.currency) }}
                    <template v-if="item.per_unit !== null">
                      ·
                    </template>
                  </template>
                  <template v-if="item.per_unit !== null">
                    {{ formatMoney(item.per_unit, item.currency) }} × {{ formatNumber(item.quantity) }}
                  </template>
                </p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </section>
</template>
